import { useCallback, useEffect, useState } from "react";
import { ADMIN_ORGS, tenantPath } from "../../lib/paths.js";
import { useAtomValue } from "jotai";
import { Navigate, NavLink, Outlet, useLocation, useParams } from "react-router-dom";
import { apiCall } from "../../lib/api-client.js";
import {
  ApiError,
  type Organization,
  type OrgOnboarding,
} from "../../lib/api-types.js";
import { authAtom } from "../../lib/auth-store.js";
import { useApiQuery } from "../../lib/useApiQuery.js";
import { onboardingPath } from "../../lib/api-paths.js";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../components/PageHeader.js";
import { InlineLoading } from "../../components/Spinner.js";
import { ErrorBanner } from "../../components/ErrorBanner.js";
import { StatusPill } from "../../components/StatusPill.js";
import { cn } from "../../lib/cn.js";
import type { OrgDetailContext } from "./context.js";

/**
 * Phase C.2 — OrganizationDetail 改 left sub-nav + Outlet。
 * 7 项导航：基本信息 / 用户 / 角色 / 审批人 / 飞书 / Bootstrap / 审计日志。
 *
 * 子路由通过 useOutletContext<OrgDetailContext>() 读 org + setOrg。
 *
 * 路由形态：/organizations/:code/<subpath>
 * 老路径 /organizations/:code 在 routes/index.tsx 用 index → redirect 到 basic-info。
 */

/**
 * 侧边栏：组织搭建 wizard（冷启动引导）置顶，其下为运营态编辑页。
 * 启动态的「飞书集成 / 飞书部门树 / 角色推荐」已折叠进 wizard（旧路由 redirect 到对应步）。
 */
interface SubNavItem {
  to: string;
  label?: string;
  labelKey?: string;
  group?: boolean;
  /** 平台运维面（迁移工具）— org-admin 隐藏，后端 requireSuper 403 兜底。
   *  其余项 org-admin 与 super/support 全可见（2026-07-07 用户规则：
   *  超管=任何组织的管理员，管理员能看能操作的面超管一律可见）。 */
  superOnly?: boolean;
}

const SUB_NAV: ReadonlyArray<SubNavItem> = [
  { to: "getting-started", label: "开始使用" },
  { to: "onboarding", label: "组织搭建" },
  { to: "basic-info", label: "基本信息", group: true },
  { to: "users", label: "用户" },
  { to: "roles", label: "角色" },
  { to: "leadership", label: "审批人" },
  { to: "project-admins", label: "项目管理员" },
  { to: "reviews", label: "审批中心" },
  { to: "tasks", label: "自动化任务" },
  { to: "integrations", label: "集成与凭据" },
  { to: "models", label: "企业模型" },
  { to: "capabilities", labelKey: "admin.organizationCapabilities.nav" },
  { to: "usage", label: "用量统计" },
  { to: "recharge", label: "充值记录" },
  { to: "llm-config", label: "LLM 配置" },
  { to: "migration", label: "迁移工具", superOnly: true },
  { to: "bootstrap", label: "Bootstrap 预览" },
  { to: "audit", label: "审计日志" },
];

export function OrganizationDetail(): JSX.Element {
  const { t } = useTranslation();
  const { code } = useParams<{ code: string }>();
  const auth = useAtomValue(authAtom);
  const [org, setOrg] = useState<Organization | null>(null);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();
  const isOrgAdmin = auth?.scope === "org-admin";

  const reload = useCallback(async () => {
    if (!auth || !code) return;
    try {
      const data = await apiCall<Organization>(
        "GET",
        `/api/organizations/${code}`,
        { token: auth.token },
      );
      setOrg(data);
      setError(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setError("组织不存在或已被删除");
      } else {
        const msg = err instanceof ApiError ? err.message : "加载失败";
        setError(msg);
      }
    }
  }, [auth, code]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // 组织搭建是否未完成 → 侧边栏「组织搭建」项显示待完成圆点
  const { data: onboarding } = useApiQuery<OrgOnboarding>(
    code ? onboardingPath(code) : null,
    [code],
  );
  const onboardingIncomplete = onboarding != null && onboarding.completedAt == null;

  // 入口分离守卫（UX 层；硬门在服务端 requireOrgAccess 403）：
  // org-admin 落在平台路径 /organizations/... → 弹去自己的租户入口（保留子页）；
  // 租户路径下跨 org → 弹回自己入口。
  if (isOrgAdmin && auth?.orgCode) {
    // 前缀字符串比对而非按段数 slice —— 不隐式耦合 ADMIN_ORGS 的路径深度。
    const orgAdminsPrefix = `${ADMIN_ORGS}/${code}`;
    if (location.pathname === orgAdminsPrefix || location.pathname.startsWith(`${orgAdminsPrefix}/`)) {
      const sub = location.pathname.slice(orgAdminsPrefix.length).replace(/^\//, "");
      return <Navigate to={tenantPath(auth.orgCode, sub)} replace />;
    }
    if (code !== auth.orgCode) {
      return <Navigate to={tenantPath(auth.orgCode)} replace />;
    }
  }

  if (error) {
    return (
      <div>
        <PageHeader title={`组织：${code ?? "?"}`} />
        <ErrorBanner>{error}</ErrorBanner>
      </div>
    );
  }

  if (!org) {
    return (
      <div>
        <PageHeader title={`组织：${code ?? "?"}`} />
        <InlineLoading />
      </div>
    );
  }

  const ctx: OrgDetailContext = { org, setOrg };

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title={org.name}
        subtitle={
          <>
            <span className="font-mono mr-2">{org.code}</span>
            <StatusPill status={org.status} />
          </>
        }
        actions={
          isOrgAdmin ? undefined : (
            <NavLink
              to={ADMIN_ORGS}
              className="text-sm text-dosia-text-secondary hover:underline"
            >
              ← 返回列表
            </NavLink>
          )
        }
      />

      <div className="flex flex-1 min-h-0 gap-6">
        <nav className="w-40 shrink-0 border-r border-dosia-border pr-2 flex flex-col gap-0.5">
          {SUB_NAV.filter((item) => !(isOrgAdmin && item.superOnly)).map((item, idx) => (
            <div key={item.to}>
              {item.group && idx > 0 ? (
                <div className="my-1 border-t border-dosia-border-light" />
              ) : null}
              <NavLink
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    "flex items-center justify-between rounded-md px-3 py-1.5 text-sm",
                    isActive
                      ? "bg-dosia-accent/15 text-dosia-text font-medium"
                      : "text-dosia-text-secondary hover:bg-dosia-bg-lighter",
                  )
                }
              >
                <span>{item.labelKey ? t(item.labelKey) : item.label}</span>
                {item.to === "onboarding" && onboardingIncomplete ? (
                  <span
                    className="h-1.5 w-1.5 shrink-0 rounded-full bg-dosia-accent"
                    title="待完成"
                  />
                ) : null}
              </NavLink>
            </div>
          ))}
        </nav>
        <div className="flex-1 min-w-0">
          <Outlet context={ctx} />
        </div>
      </div>
    </div>
  );
}
